import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth.store.js";
import { routeMetaForName } from "@/constants/navModules.js";
import { maskOrganizationRouteParam } from "@/router/onCallRouteGuards.js";

const AppShell = () => import("@/components/layout/AppShell.vue");
const LoginView = () => import("@/views/auth/LoginView.vue");
const DashboardView = () => import("@/views/dashboard/DashboardView.vue");
const OrganizationsView = () => import("@/views/organizations/OrganizationsView.vue");
const OrganizationDetailView = () => import("@/views/organizations/OrganizationDetailView.vue");
const OrganizationOnCallActionsView = () =>
  import("@/views/organizations/OrganizationOnCallActionsView.vue");
const OrganizationOnCallActionWizardView = () =>
  import("@/views/organizations/OrganizationOnCallActionWizardView.vue");
const OrganizationOrgDefaultsView = () =>
  import("@/views/organizations/OrganizationOrgDefaultsView.vue");
const CallsView = () => import("@/views/calls/CallsView.vue");
// const AiAgentsView = () => import("@/views/ai-agents/AiAgentsView.vue");
// const KnowledgeBaseView = () => import("@/views/knowledge-base/KnowledgeBaseView.vue");
// const AnalyticsView = () => import("@/views/analytics/AnalyticsView.vue");
const UsersView = () => import("@/views/users/UsersView.vue");
const SipNumbersView = () => import("@/views/sip-numbers/SipNumbersView.vue");
// Contact Us module inactive — APIs removed; keep view code for future re-enable
// const ContactUsView = () => import("@/views/contact-us/ContactUsView.vue");
const CallersView = () => import("@/views/callers/CallersView.vue");
const TransactionHistoryView = () => import("@/views/transactions/TransactionHistoryView.vue");
const AuditLogsView = () => import("@/views/audit-logs/AuditLogsView.vue");
const SettingsView = () => import("@/views/settings/SettingsView.vue");
const ChannelDashboardView = () => import("@/views/channel-dashboard/ChannelDashboardView.vue");
const ActionTemplatesView = () => import("@/views/actionTemplates/ActionTemplatesView.vue");

const routes = [
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { public: true },
  },
  {
    path: "/",
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      { path: "dashboard", name: "dashboard", component: DashboardView, meta: routeMetaForName("dashboard") },
      { path: "organizations", name: "organizations", component: OrganizationsView, meta: routeMetaForName("organizations") },
      {
        path: "organizations/:id",
        name: "organization-detail",
        component: OrganizationDetailView,
        meta: routeMetaForName("organizations"),
      },
      {
        path: "organizations/:id/on-call-actions",
        name: "organization-on-call-actions",
        component: OrganizationOnCallActionsView,
        beforeEnter: maskOrganizationRouteParam,
        meta: {
          ...routeMetaForName("organizations"),
          pageTitle: "On-call actions",
          searchPlaceholder: "Search on-call actions, types, descriptions...",
        },
      },
      {
        path: "organizations/:id/on-call-actions/new",
        name: "organization-on-call-action-new",
        component: OrganizationOnCallActionWizardView,
        beforeEnter: maskOrganizationRouteParam,
        meta: {
          ...routeMetaForName("organizations"),
          pageTitle: "Create on-call action",
        },
      },
      {
        path: "organizations/:id/on-call-actions/:actionId/edit",
        name: "organization-on-call-action-edit",
        component: OrganizationOnCallActionWizardView,
        beforeEnter: maskOrganizationRouteParam,
        meta: {
          ...routeMetaForName("organizations"),
          pageTitle: "Edit on-call action",
        },
      },
      {
        path: "organizations/:id/default-fields",
        name: "organization-default-fields",
        component: OrganizationOrgDefaultsView,
        beforeEnter: maskOrganizationRouteParam,
        meta: {
          ...routeMetaForName("organizations"),
          pageTitle: "Update fields",
          searchPlaceholder: "Search fields, keys, types...",
        },
      },
      {
        path: "organizations/:id/lead-statuses",
        name: "organization-lead-statuses",
        component: OrganizationOrgDefaultsView,
        beforeEnter: maskOrganizationRouteParam,
        meta: {
          ...routeMetaForName("organizations"),
          pageTitle: "Update lead statuses",
          searchPlaceholder: "Search lead statuses...",
        },
      },
      {
        path: "organizations/:id/defaults",
        redirect: (to) => ({
          name: "organization-default-fields",
          params: to.params,
          query: to.query,
          hash: to.hash,
        }),
      },
      { path: "calls", name: "calls", component: CallsView, meta: routeMetaForName("calls") },
      // { path: "ai-agents", name: "ai-agents", component: AiAgentsView },
      // { path: "knowledge-base", name: "knowledge-base", component: KnowledgeBaseView },
      // { path: "analytics", name: "analytics", component: AnalyticsView },
      { path: "users", name: "users", component: UsersView, meta: routeMetaForName("users") },
      { path: "sip-numbers", name: "sip-numbers", component: SipNumbersView, meta: routeMetaForName("sip-numbers") },
      // { path: "contact-us", name: "contact-us", component: ContactUsView, meta: routeMetaForName("contact-us") },
      { path: "callers", name: "callers", component: CallersView, meta: routeMetaForName("callers") },
      { path: "transactions", name: "transactions", component: TransactionHistoryView, meta: routeMetaForName("transactions") },
      { path: "audit-logs", name: "audit-logs", component: AuditLogsView, meta: routeMetaForName("audit-logs") },
      { path: "settings", name: "settings", component: SettingsView, meta: routeMetaForName("settings") },
      { path: "channel-dashboard", name: "channel-dashboard", component: ChannelDashboardView, meta: routeMetaForName("channel-dashboard") },
      { path: "action-templates", name: "action-templates", component: ActionTemplatesView, meta: routeMetaForName("action-templates") },
      { path: "", redirect: { name: "organizations" } },
    ],
  },
  { path: "/:pathMatch(.*)*", redirect: { name: "login" } },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();

  if (!authStore.sessionReady) {
    await authStore.initSession();
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: "login" };
  }

  if (to.name === "login" && authStore.isAuthenticated) {
    return { name: "organizations" };
  }

  if (to.path === "/" && !to.name && authStore.isAuthenticated) {
    return { name: "organizations" };
  }

  return true;
});

export default router;
